/**
 * Session-stable seeded shuffle for the Explore feed.
 *
 * The feed order must look randomized, but it must NOT change while the user
 * is actively using the app (scrolling, switching category chips, switching
 * between Wallpapers / Live Wallpapers / Ringtones, opening a detail view and
 * coming back, or opening/closing search). It must ONLY change on a full app
 * restart.
 *
 * How this works:
 * - SESSION_SEED is created once, at module load (i.e. once per app
 *   session), and lives in memory only — it is never written to
 *   localStorage, sessionStorage, or the database. A full restart re-executes
 *   this module and produces a brand-new seed.
 * - Each item's sort position is derived deterministically from
 *   (SESSION_SEED, item.id) via a small seeded PRNG (mulberry32). Because the
 *   position only depends on stable inputs, filtering a shuffled list down to
 *   a category subset, or re-deriving it after a re-render, always yields the
 *   same relative order for the items that remain — nothing needs to be
 *   cached in state/refs/storage to stay stable.
 */

// Generated once per module load (i.e. once per app session). Intentionally
// not persisted anywhere — a full app restart re-runs this line and yields a
// new value, which is exactly what makes the feed reshuffle on restart.
const SESSION_SEED = Math.floor(Math.random() * 0xffffffff) >>> 0;

// mulberry32 — small, fast, deterministic PRNG given a numeric seed.
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Deterministic string hash (FNV-1a), used to fold an item id into a numeric
// seed for mulberry32.
function hashId(id: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < id.length; i += 1) {
    hash ^= id.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/**
 * Deterministic per-item sort key for the current app session: stable for a
 * given id as long as SESSION_SEED hasn't changed (i.e. no full app restart
 * happened), but effectively random-looking and different every session.
 */
function sessionSortKey(id: string): number {
  return mulberry32((hashId(id) ^ SESSION_SEED) >>> 0)();
}

/**
 * Returns a new array in session-stable shuffled order. Safe to call on any
 * filtered subset of a larger list — each item's position is derived only
 * from its own id plus the session seed, so filtering/re-deriving a subset
 * never reorders items relative to each other, and no items are dropped or
 * duplicated (it's a pure sort of the input array).
 */
export function sessionShuffle<T extends { id: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => sessionSortKey(a.id) - sessionSortKey(b.id));
}
